// The position on the board, written the way the rest of chess writes it:
// FEN for where it stands, PGN for how it got there.
//
// Every screen with a board offers both behind its ⋯, so a position can leave
// the app without being typed out again — into an engine, a database, a
// message to somebody. Nothing here touches the page: it is the board's
// formats, and the copying itself is app/copy.ts.

import { Chess } from "chess.js";
import { isUci, playUci } from "./lines.ts";

/** What is on the board, and how it came to be there. */
export interface Shown {
  /** The position on the board. */
  fen: string;
  /** Where its moves start: the puzzle's position, the repertoire's root,
   *  the first move of a game against the bot. */
  start: string;
  /** The moves from `start` to `fen`, SAN or UCI, in order. */
  moves: string[];
  /** What the PGN is to say about where this came from. Empty values are
   *  left out, so a screen can pass what it happens to know. */
  headers?: Record<string, string | number | null | undefined>;
}

/** The headers a PGN carries, in the order the standard writes them. */
const ORDER = ["Event", "Site", "Date", "Round", "White", "Black", "Result"];

/** A date as a PGN writes one: 2026.09.23, from unix seconds. */
export function pgnDate(unix?: number | null): string {
  if (!unix) return "";
  const at = new Date(unix * 1000);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}.${two(at.getMonth() + 1)}.${two(at.getDate())}`;
}

function play(game: Chess, move: string): boolean {
  if (isUci(move)) return Boolean(playUci(game, move));
  try {
    // SAN as the screens hold it, which may have lost its check mark.
    return Boolean(game.move(move) || game.move(move, { sloppy: true }));
  } catch {
    return false;
  }
}

/**
 * The position on the board and the moves to it, as one PGN. A line that
 * starts anywhere but move one carries its position in a FEN header, which
 * is what every reader of a PGN expects; a line whose moves cannot all be
 * played stops where they stop, so what comes out is always a real game.
 */
export function pgnOf(shown: Shown): string {
  const game = new Chess(shown.start || shown.fen);
  let played = 0;
  for (const move of shown.moves || []) {
    if (!play(game, move)) break;
    played += 1;
  }
  const headers: Record<string, string | number | null | undefined> = { Event: "Blundermine", Result: "*", ...(shown.headers || {}) };
  const keys = [...ORDER.filter((k) => k in headers), ...Object.keys(headers).filter((k) => !ORDER.includes(k))];
  for (const key of keys) {
    const value = headers[key];
    if (value != null && String(value) !== "") game.header(key, String(value));
  }
  const text = game.pgn({ max_width: 76, newline_char: "\n" });
  // A position with no moves yet is a whole PGN too — the headers and the
  // result on its own. chess.js leaves out the empty line between the two
  // when there is no move list, and a PGN missing it cannot be read back.
  return played ? text : text.replace(/\n([^[\n]+)$/, "\n\n$1");
}
